import {BasePopup} from "components/popup/BasePopup"
import styles from "./BatchMakeupAttendanceCheckin.module.css"
import {BaseButton, BaseLoading, BaseTextArea, KlassDropdown, SecondaryButton} from "components"
import {Icon} from "components/Icon"
import {BaseDatePicker, BaseTimePicker} from "components/DateTimePicker"
import {Search} from "components/inputs/Search"
import moment, {Moment} from "moment"
import {useModel, useMutation, useVisible} from "hooks"
import {Fragment, useCallback, useEffect, useMemo, useRef, useState} from "react"
import cx from "classnames"
import {cloneDeep, get} from "lodash"
import {handleError, toastError, toastInfo, toastSuccess} from "helpers"
import {academicPlansService} from "services"
import {AttendanceEvent, AttendanceEventStatus, AttendanceEventType} from "types/attendance"
import {Auth} from "types/auth"
import {KlassappTable, KlassappTableHeader, SignaturePopup} from "uiKit"
import {KlassappTableHOC} from "HOC"
import {KlassappTableProps} from "types/common"
import {useDebounce} from "react-use"

const EVENT_OPTIONS: Array<{name: string; value: AttendanceEventType}> = [
    {name: "Checkin", value: AttendanceEventType.Checkin},
    {name: "Checkout", value: AttendanceEventType.Checkout}
]

const SEARCH_DEBOUNCE_MS = 400

type BatchRowResult = {
    status: "success" | "skipped" | "failed"
    message: string
}

export type BatchMakeupStudentAttendance = {
    attendanceId?: number
    academicMakeupStudentId: number
    lessonId?: number | null
    events: AttendanceEvent[]
}

export type BatchMakeupStudent = {
    id: number
    profileId: number
    academicMakeupStudentId?: number
    lessonId?: number | null
    customProfileId?: string
    userId?: number | string
    studentName: string
    timezone: string
    reason?: string
    events: AttendanceEvent[]
    attendanceId?: number
    attendances: BatchMakeupStudentAttendance[]
}

type Props = {
    isShow: boolean
    onClose: () => void
    onSuccess: () => void
} & KlassappTableProps

const mapApiItemToStudent = (item: any): BatchMakeupStudent | null => {
    const profileId = item.profileId
    if (!profileId) return null
    const attendances: BatchMakeupStudentAttendance[] = (item.attendances ?? []).map((attendance: any) => ({
        attendanceId: attendance.attendanceId,
        academicMakeupStudentId: attendance.academicMakeupStudentId,
        lessonId: attendance.lessonId ?? null,
        events: attendance.events ?? []
    }))
    return {
        id: profileId,
        profileId,
        academicMakeupStudentId: item.academicMakeupStudentId,
        lessonId: item.lessonId ?? null,
        customProfileId: item.customProfileId ?? "",
        userId: item.userId,
        studentName: item.studentName || `${item.firstName ?? ""} ${item.lastName ?? ""}`.trim(),
        timezone: item.timezone || moment.tz.guess(),
        reason: item.reason ?? "",
        events: item.events ?? [],
        attendanceId: item.attendanceId,
        attendances
    }
}

function BatchMakeupAttendanceCheckinTable(props: Props) {
    const {
        isShow,
        onClose,
        onSuccess,
        data,
        page,
        total,
        pageSize,
        columns,
        fields,
        allFields,
        isLoading,
        dispatch,
        getCurrentData
    } = props

    const model = useModel()
    const dateFormat = model.getUserDateFormat()
    const timeFormat = model.getUserTimeFormat()
    const signaturePopup = useVisible()

    const [selectedDate, setSelectedDate] = useState<Moment | null>(moment())
    const [selectedTime, setSelectedTime] = useState<Moment | null>(moment())
    const [selectedEventType, setSelectedEventType] = useState<{name: string; value: AttendanceEventType} | null>(null)
    const [notes, setNotes] = useState("")
    const [search, setSearch] = useState("")
    const [searchDebounce, setSearchDebounce] = useState("")
    const [selectedCount, setSelectedCount] = useState(0)
    const [sharedSignature, setSharedSignature] = useState<{
        signatureId: number
        imageUrl: string
        userId: number
    }>()

    const wasOpenRef = useRef(false)
    const batchResultsRef = useRef<Record<number, BatchRowResult>>({})
    const selectedMapRef = useRef<Record<number, BatchMakeupStudent>>({})
    const searchDebounceRef = useRef(searchDebounce)
    const selectedDateRef = useRef(selectedDate)

    useDebounce(() => setSearchDebounce(search.trim()), SEARCH_DEBOUNCE_MS, [search])

    const selectedDateKey = selectedDate?.format("YYYY-MM-DD") ?? ""

    useEffect(() => {
        searchDebounceRef.current = searchDebounce
    }, [searchDebounce])

    useEffect(() => {
        selectedDateRef.current = selectedDate
    }, [selectedDate])

    const syncSelectedCount = useCallback(() => {
        setSelectedCount(Object.keys(selectedMapRef.current).length)
    }, [])

    const clearAllSelection = useCallback(() => {
        selectedMapRef.current = {}
        syncSelectedCount()
        const currentData = getCurrentData?.() ?? data ?? []
        dispatch({
            data: cloneDeep(currentData).map((item) => ({...item, isChecked: false}))
        })
    }, [data, dispatch, getCurrentData, syncSelectedCount])

    const renderBatchResult = useCallback((batchResult?: BatchRowResult | null) => {
        if (!batchResult) {
            return <span className={styles.resultEmpty}>—</span>
        }
        return (
            <span
                className={cx(styles.resultBadge, {
                    [styles.resultSuccess]: batchResult.status === "success",
                    [styles.resultSkipped]: batchResult.status === "skipped",
                    [styles.resultFailed]: batchResult.status === "failed"
                })}
                title={batchResult.message}>
                {batchResult.message}
            </span>
        )
    }, [])

    const getColumns = useCallback(() => {
        return [
            {
                title: "Enrollment Id",
                field: "customProfileId"
            },
            {
                title: "User Id",
                field: "userId"
            },
            {
                title: "Student",
                field: "studentName"
            },
            {
                title: "Result",
                field: "batchResultHtml"
            }
        ]
    }, [])

    const getFields = useCallback(() => {
        return ["Enrollment Id", "User Id", "Student", "Result"]
    }, [])

    const getListData = useCallback(async () => {
        const keyword = searchDebounceRef.current
        if (!keyword) {
            const selectedRows = Object.values(selectedMapRef.current).map((item) => ({
                ...item,
                isChecked: true,
                batchResult: batchResultsRef.current[item.profileId] ?? null,
                batchResultHtml: renderBatchResult(batchResultsRef.current[item.profileId] ?? null)
            }))
            dispatch({data: selectedRows, total: selectedRows.length, isLoading: false})
            return
        }

        dispatch({isLoading: true})
        try {
            const date = selectedDateRef.current?.format("YYYY-MM-DD") || moment().format("YYYY-MM-DD")
            const {data: rows, total: listTotal} = await academicPlansService.listBatchMakeupCheckinStudents({
                search: keyword,
                date,
                range: {page: props.page, pageSize: props.pageSize}
            })

            const resultsMap = batchResultsRef.current
            const selectedMap = selectedMapRef.current
            const mapped = (rows || [])
                .map(mapApiItemToStudent)
                .filter((item): item is BatchMakeupStudent => !!item)
                .map((item) => {
                    const batchResult = resultsMap[item.profileId] ?? null
                    return {
                        ...item,
                        isChecked: !!selectedMap[item.profileId],
                        batchResult,
                        batchResultHtml: renderBatchResult(batchResult)
                    }
                })

            mapped.forEach((item) => {
                if (selectedMap[item.profileId]) {
                    selectedMap[item.profileId] = item
                }
            })

            dispatch({data: mapped, total: listTotal ?? 0})
        } catch (err) {
            handleError(err)
            dispatch({data: [], total: 0})
        } finally {
            dispatch({isLoading: false})
        }
    }, [dispatch, props.page, props.pageSize, renderBatchResult])

    useEffect(() => {
        dispatch({isClassComponent: false})
        props.dispatchFunc([
            {key: "getListData", func: getListData},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns}
        ])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!isShow) {
            wasOpenRef.current = false
            batchResultsRef.current = {}
            selectedMapRef.current = {}
            setSelectedCount(0)
            setSelectedDate(moment())
            setSelectedTime(moment())
            setSelectedEventType(null)
            setNotes("")
            setSearch("")
            setSearchDebounce("")
            setSharedSignature(undefined)
            dispatch({data: [], total: 0, page: 1})
            return
        }
        if (!wasOpenRef.current) {
            wasOpenRef.current = true
            setSelectedDate(moment())
            setSelectedTime(moment())
            setSelectedEventType(null)
            setNotes("")
            setSearch("")
            setSearchDebounce("")
            setSharedSignature(undefined)
            batchResultsRef.current = {}
            selectedMapRef.current = {}
            setSelectedCount(0)
            dispatch({data: [], total: 0, page: 1})
        }
    }, [isShow, dispatch])

    useEffect(() => {
        if (!isShow) return
        if (!searchDebounce) {
            if (page !== 1) {
                props.onChangePage(1)
                return
            }
            getListData()
            return
        }
        if (page !== 1) {
            props.onChangePage(1)
            return
        }
        getListData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isShow, searchDebounce, selectedDateKey])

    useEffect(() => {
        if (!isShow || !searchDebounce) return
        getListData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize])

    const getCreatedAtForStudent = (timezone: string) => {
        const datePart = selectedDate?.clone() || moment()
        const timePart = selectedTime?.clone() || moment()
        const combined = datePart.clone().set({
            hour: timePart.hour(),
            minute: timePart.minute(),
            second: 0,
            millisecond: 0
        })
        return moment(combined).tz(timezone, true).toISOString()
    }

    const onUpdateRowData = (updatedData) => {
        const currentData = getCurrentData()
        const newData = cloneDeep(currentData).map((item) => {
            if (item.id === updatedData.id) {
                return {...item, isChecked: updatedData.isChecked}
            }
            return item
        })

        if (updatedData.isChecked) {
            selectedMapRef.current[updatedData.profileId] = updatedData
        } else {
            delete selectedMapRef.current[updatedData.profileId]
        }
        syncSelectedCount()
        dispatch({data: newData})
    }

    const onUpdateTableData = (newData) => {
        const previousById = new Map((getCurrentData() || []).map((item) => [item.id, item]))
        cloneDeep(newData).forEach((item) => {
            const prev = previousById.get(item.id)
            if (!prev) return
            if (item.isChecked && !prev.isChecked) {
                selectedMapRef.current[item.profileId] = item
            } else if (!item.isChecked && prev.isChecked) {
                delete selectedMapRef.current[item.profileId]
            }
        })
        syncSelectedCount()
        dispatch({data: newData})
    }

    const batchMutation = useMutation(
        async () => {
            if (!selectedDate) {
                throw new Error("Please select a date")
            }
            if (!selectedTime) {
                throw new Error("Please select a time")
            }
            if (!selectedEventType?.value) {
                throw new Error("Please select an event type")
            }
            if (!sharedSignature?.signatureId) {
                throw new Error("Please provide a shared signature")
            }

            const selectedStudents = Object.values(selectedMapRef.current)
            if (!selectedStudents.length) {
                throw new Error("Please select at least one student")
            }

            const response = await academicPlansService.batchCreateMakeupAttendanceEvents({
                eventType: selectedEventType.value,
                signatureId: sharedSignature.signatureId,
                reason: notes || "",
                students: selectedStudents.map((row) => {
                    const createdAt = getCreatedAtForStudent(row.timezone)
                    if (selectedEventType.value === AttendanceEventType.Checkout) {
                        return {
                            studentProfileId: row.profileId,
                            createdAt,
                            academicMakeupStudentId: row.academicMakeupStudentId,
                            lessonId: row.lessonId ?? null,
                            reason: notes || row.reason || "",
                            events: (row.events ?? []).map((event) => ({
                                eventType: event.eventType,
                                status: event.status ?? AttendanceEventStatus.Success,
                                createdAt: event.createdAt,
                                photoUrl: event.photoUrl
                            }))
                        }
                    }
                    return {
                        studentProfileId: row.profileId,
                        createdAt,
                        reason: notes || ""
                    }
                })
            })

            const results = response?.results ?? []
            const nextResults: Record<number, BatchRowResult> = {...batchResultsRef.current}

            results.forEach((result: any) => {
                const profileId = result?.studentProfileId
                if (!profileId) return
                if (result.skipped) {
                    nextResults[profileId] = {
                        status: "skipped",
                        message: result.skipReason || "Skipped"
                    }
                } else if (!result.success) {
                    nextResults[profileId] = {
                        status: "failed",
                        message: result.error || "Failed"
                    }
                } else {
                    nextResults[profileId] = {
                        status: "success",
                        message: "Updated"
                    }
                }
            })

            return {
                nextResults,
                successCount: response?.successCount ?? 0,
                skippedCount: response?.skippedCount ?? 0,
                failedCount: response?.failedCount ?? 0
            }
        },
        {
            onSuccess: async ({nextResults, successCount, skippedCount, failedCount}) => {
                batchResultsRef.current = nextResults
                // Keep selection so user can still see Result on submitted students

                if (failedCount > 0 && (successCount > 0 || skippedCount > 0)) {
                    toastInfo(
                        `Updated ${successCount}. Skipped ${skippedCount}. Failed ${failedCount}. See Result column.`
                    )
                } else if (failedCount > 0) {
                    toastError(`Failed ${failedCount} student(s). See Result column.`)
                } else if (skippedCount > 0 && successCount === 0) {
                    toastInfo(`Skipped ${skippedCount} student(s). See Result column.`)
                } else if (skippedCount > 0) {
                    toastInfo(`Updated ${successCount}. Skipped ${skippedCount}. See Result column.`)
                } else {
                    toastSuccess("Batch makeup attendance updated successfully")
                }

                await getListData()
                onSuccess()
            },
            onError: (err) => {
                if (err instanceof Error && !get(err, "response")) {
                    toastError(err.message)
                    return
                }
                handleError(err)
            }
        }
    )

    const onSubmit = () => {
        if (!selectedDate) {
            toastError("Please select a date")
            return
        }
        if (!selectedTime) {
            toastError("Please select a time")
            return
        }
        if (!selectedEventType?.value) {
            toastError("Please select an event type")
            return
        }
        if (!sharedSignature?.signatureId) {
            toastError("Please provide a shared signature")
            signaturePopup.open()
            return
        }
        if (!Object.keys(selectedMapRef.current).length) {
            toastError("Please select at least one student")
            return
        }
        batchMutation.mutate()
    }

    const renderAttendanceEventsPanel = useCallback(
        (record: BatchMakeupStudent, expanded: boolean) => {
            const attendances = record.attendances ?? []
            if (!expanded || !attendances.length) {
                return null
            }

            const timezone = record.timezone || moment.tz.guess()

            return (
                <div className={styles.expandedAttendancePanel}>
                    <div className={styles.eventsContainer}>
                        {attendances.map((attendance, attendanceIndex) => (
                            <Fragment
                                key={attendance.attendanceId ?? attendance.academicMakeupStudentId ?? attendanceIndex}>
                                {attendance.events?.map((event, eventIndex) => (
                                    <div
                                        key={event.id ?? `${attendance.academicMakeupStudentId}-${eventIndex}`}
                                        className={styles.eventItem}>
                                        <div className={styles.event}>
                                            {event.eventType === AttendanceEventType.Checkin ? (
                                                <Icon
                                                    icon="CHECKIN_FILL"
                                                    className={styles.eventIcon}
                                                    color="#18a957"
                                                />
                                            ) : null}
                                            {event.eventType === AttendanceEventType.Checkout ? (
                                                <Icon
                                                    icon="CHECKOUT_FILL"
                                                    className={styles.eventIcon}
                                                    color="#DF1642"
                                                />
                                            ) : null}
                                            {event.eventType === AttendanceEventType.BreakEnd ? (
                                                <Icon
                                                    icon="ATTENDANCE_IN"
                                                    className={styles.eventIcon}
                                                    color="#18a957"
                                                />
                                            ) : null}
                                            {event.eventType === AttendanceEventType.BreakStart ? (
                                                <Icon
                                                    icon="ATTENDANCE_OUT"
                                                    className={styles.eventIcon}
                                                    color="#DF1642"
                                                />
                                            ) : null}
                                            <span className={styles.eventTime}>
                                                {moment(event.createdAt).tz(timezone).format(timeFormat)}
                                            </span>
                                        </div>
                                        {eventIndex !== (attendance.events?.length ?? 0) - 1 ? (
                                            <div className={styles.eventSeparator} />
                                        ) : null}
                                    </div>
                                ))}
                                {attendanceIndex !== attendances.length - 1 ? (
                                    <div className={styles.eventSeparator} />
                                ) : null}
                            </Fragment>
                        ))}
                    </div>
                </div>
            )
        },
        [timeFormat]
    )

    const expandable = useMemo(
        () => ({
            isExpanded: true,
            rowExpandable: (record: BatchMakeupStudent) => !!(record.attendances ?? []).length,
            expandedRowRender: renderAttendanceEventsPanel
        }),
        [renderAttendanceEventsPanel]
    )

    const headerActions = useMemo(
        () => [
            {
                title: `Clear (${selectedCount})`,
                icon: "CLOSE",
                action: clearAllSelection
            }
        ],
        [clearAllSelection, selectedCount]
    )

    return (
        <BasePopup isShow={isShow} onClose={onClose} width="90vw" leftIcon="PERSON_ADD" destroyOnClose keyboard={false}>
            <div className={styles.container}>
                <div className={styles.formTitle}>Batch Makeup Check-In / Check-Out</div>

                <div className={styles.timeRow}>
                    <span className={styles.timeLabel}>
                        Date<span className={styles.star}>*</span>
                    </span>
                    <BaseDatePicker
                        style={{width: 160}}
                        value={selectedDate}
                        format={dateFormat}
                        allowClear={false}
                        onChange={(value) => setSelectedDate(value)}
                        placeholder={dateFormat}
                    />
                    <span className={styles.timeLabel}>
                        Time<span className={styles.star}>*</span>
                    </span>
                    <BaseTimePicker
                        style={{width: 160}}
                        value={selectedTime}
                        format={timeFormat}
                        allowClear={false}
                        onChange={(value) => setSelectedTime(value)}
                        placeholder="Time"
                    />
                    <span className={styles.timeLabel}>
                        Event Type<span className={styles.star}>*</span>
                    </span>
                    <div className={styles.eventTypeSelect}>
                        <KlassDropdown
                            options={EVENT_OPTIONS}
                            value={selectedEventType}
                            onChange={setSelectedEventType}
                            placeholder="Select event type"
                            valueKey="value"
                            labelKey="name"
                            isClearable={false}
                        />
                    </div>
                </div>

                <div className={styles.signatureRow}>
                    <span className={styles.timeLabel}>
                        Shared Signature<span className={styles.star}>*</span>
                    </span>
                    {sharedSignature?.imageUrl ? (
                        <div className={styles.signaturePreview}>
                            <img src={sharedSignature.imageUrl} alt="signature" className={styles.signatureImg} />
                            <SecondaryButton title="Re-sign" onClick={signaturePopup.open} />
                        </div>
                    ) : (
                        <BaseButton title="Sign" onClick={signaturePopup.open} />
                    )}
                </div>

                <div className={styles.notesRow}>
                    <BaseTextArea value={notes} onChange={setNotes} placeholder="Notes / reason (optional)" rows={2} />
                </div>

                <p className={styles.hint}>Search students, then select with checkboxes. Events show under each row.</p>
                <div className={styles.searchRow}>
                    <div className={styles.searchBox}>
                        <Search value={search} onChange={setSearch} placeholder="Search students" />
                    </div>
                    {selectedCount > 0 ? (
                        <SecondaryButton title={`Clear selection (${selectedCount})`} onClick={clearAllSelection} />
                    ) : null}
                </div>

                <div className={styles.tableContainer}>
                    <KlassappTableHeader
                        page={page}
                        total={total}
                        defaultPageSize={pageSize}
                        onChangePage={props.onChangePage}
                        onChangeRowPerPage={props.onChangeRowPerPage}
                        isShowAction={selectedCount > 0}
                        actions={headerActions}
                    />
                    <KlassappTable
                        columns={columns}
                        data={data}
                        isLoading={isLoading}
                        fields={fields}
                        allFields={allFields}
                        isShowCheckedColumn
                        isShowCheckedColumnHeader
                        isShowScrollTable
                        onChangeFields={props.onChangeFields}
                        onUpdateRowData={onUpdateRowData}
                        onUpdateTableData={onUpdateTableData}
                        onDraggableColumn={props.onDraggableColumn}
                        onChangeAllFields={props.onChangeAllFields}
                        expandable={expandable}
                    />
                </div>

                <div className={styles.actionWrap}>
                    <SecondaryButton title="Cancel" onClick={onClose} />
                    <BaseButton
                        title={`Submit (${selectedCount})`}
                        onClick={onSubmit}
                        loading={batchMutation.isLoading}
                        disabled={batchMutation.isLoading || selectedCount <= 0}
                    />
                </div>
            </div>

            <BaseLoading isShow={batchMutation.isLoading} />

            <SignaturePopup
                isShow={signaturePopup.isVisible}
                onClose={signaturePopup.close}
                onSetNewSignature={(signature) => {
                    setSharedSignature(signature)
                    signaturePopup.close()
                }}
                actions={{
                    onCancel: signaturePopup.close,
                    onSign: (signature) => {
                        setSharedSignature(signature)
                        signaturePopup.close()
                    }
                }}
                autoClose
                useOneTimeSignature
                targetUser={{
                    type: (model.getCurrentUserProfile()?.type as Auth.UserProfileType) ?? Auth.UserProfileType.Staff,
                    id: model.user?.id,
                    profileId: model.profileId
                }}
                title="Shared Batch Signature"
            />
        </BasePopup>
    )
}

export const BatchMakeupAttendanceCheckin = KlassappTableHOC(BatchMakeupAttendanceCheckinTable, {
    page: 1,
    pageSize: 20
})
